import styled from 'styled-components';
import { windowMaxWidth } from '../breakpoints';

const AccordionBadge = styled.span`
  align-items: center;
  background: var(--secondary-whisper);
  border-radius: var(--border-radius-extra-large);
  color: var(--greyscale-blackish);
  display: inline-flex;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-small);
  margin-left: 8px;
  padding: 2px 8px;
`;

const AccordionContent = styled.div`
  margin-top: 16px;
`;

const AccordionDescription = styled.p`
  color: var(--greyscale-shade);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
  margin: 0;
  max-width: 390px;
`;

const AccordionHeader = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  user-select: none;
`;

const AccordionHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const AccordionIconWrapper = styled.div`
  flex-shrink: 0;
  line-height: 0;
`;

const CheckboxGrid = styled.div`
  display: grid;
  gap: 20px 24px;
  grid-template-columns: 1fr 1fr;

  @media ${windowMaxWidth.splitScreen} {
    grid-template-columns: 1fr;
  }
`;

const ContentHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;

  /* 20px top so the content title lines up with the two sidebar titles (both 20px from the top). */
  padding: 20px 20px 0;
`;

const ContentIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 30px 20px;
`;

const ContentIntroMeta = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ContentPadded = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const ContentTitle = styled.h3`
  font-size: var(--font-size-heading-3);
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-medium);
  margin: 0;
`;

const FieldLabel = styled.div`
  color: var(--greyscale-shade);
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-size: var(--font-size-small);
  font-variant-numeric: lining-nums tabular-nums;
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-small);
  margin-bottom: 4px;
  padding-left: 15px;
`;

const NotificationOptionIcon = styled.div`
  flex-shrink: 0;
  line-height: 0;
`;

const NotificationTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const NotificationGridRow = styled.div`
  align-items: center;
  display: grid;
  gap: 40px;
  grid-template-columns: minmax(0, 470px) minmax(72px, 1fr) minmax(72px, 1fr);

  @media ${windowMaxWidth.splitScreen} {
    gap: 16px;
  }
`;

const NotificationHeaderRow = styled(NotificationGridRow)`
  border-bottom: 1px solid var(--greyscale-dark-white);
  padding: 10px 0;
`;

const NotificationRow = styled(NotificationGridRow)`
  border-bottom: 1px solid var(--greyscale-dark-white);
  padding: 20px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationColumnHead = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const NotificationColumnTitle = styled.span`
  color: var(--greyscale-blackish);
  font-size: var(--font-size-heading-4);
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-regular);
  white-space: nowrap;
`;

const NotificationColumnSubtitle = styled.span`
  color: var(--greyscale-shade);
  font-size: var(--font-size-small);
  line-height: var(--font-line-height-small);

  /* From split-screen down there is too little space — show only "Portal"/"E-Mail". Only applies
     under the own organization; in the other-org view the subtitles are not in the markup anyway. */
  @media ${windowMaxWidth.splitScreen} {
    display: none;
  }
`;

const NotificationRowLabel = styled.div`
  color: var(--greyscale-shade);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
`;

const NotificationDescription = styled.p`
  color: var(--greyscale-shade);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
  margin: 0;
  max-width: 470px;
`;

const NotificationCheckboxCell = styled.div`
  align-items: center;
  display: flex;
`;

const NotificationHelpLink = styled.a`
  align-items: center;
  align-self: flex-start;
  color: var(--primary-oqio-blue);
  display: inline-flex;
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-medium);
  gap: 6px;
  line-height: var(--font-line-height-regular);
  text-decoration: none;
  width: fit-content;

  &:hover {
    color: var(--primary-oqio-blue-hover);
  }
`;

const ReadOnlyRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const ReadOnlyValue = styled.span`
  color: var(--greyscale-blackish);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const SaveFooter = styled.div`
  align-items: center;
  background-color: var(--greyscale-white);
  border-top: 1px solid var(--greyscale-dark-white);
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  padding: 16px 20px;
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionCard = styled.div`
  border-bottom: 1px solid var(--greyscale-dark-white);
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
`;

const SectionDescription = styled.p`
  color: var(--greyscale-shade);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
  margin: 0;
  max-width: 540px;
`;

const SectionLeft = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 10px;
  max-width: ${({ $compact }) => ($compact ? '350px' : '470px')};
  min-width: 0;
  width: 100%;

  @media ${windowMaxWidth.laptopM} {
    max-width: ${({ $compact }) => ($compact ? '100%' : '470px')};
  }

  @media ${windowMaxWidth.splitScreen} {
    width: 100%;
  }
`;

const SectionRight = styled.div`
  flex: 1;
  min-width: 0;

  @media ${windowMaxWidth.laptopM} {
    min-width: ${({ $compact }) => ($compact ? '100%' : '0')};
  }

  @media ${windowMaxWidth.splitScreen} {
    min-width: 100%;
  }
`;

const SectionRightContent = styled.div`
  max-width: 550px;

  @media ${windowMaxWidth.splitScreen} {
    max-width: 100%;
  }
`;

const SectionRow = styled.div`
  align-items: ${({ $alignCenter }) => ($alignCenter ? 'center' : 'flex-start')};
  display: flex;
  gap: 40px;
  justify-content: space-between;
  ${({ $mt }) => $mt != null && `margin-top: ${$mt}px;`}

  > :first-child {
    padding-top: ${({ $withFieldOffset }) => ($withFieldOffset ? '29px' : '0')};
  }

  @media ${windowMaxWidth.laptopM} {
    flex-direction: ${({ $compact }) => ($compact ? 'column' : 'row')};
    gap: ${({ $compact }) => ($compact ? '16px' : '40px')};
    justify-content: ${({ $compact }) => ($compact ? 'flex-start' : 'space-between')};

    > :first-child {
      padding-top: ${({ $compact, $withFieldOffset }) => ($compact || !$withFieldOffset ? '0' : '29px')};
    }
  }

  @media ${windowMaxWidth.splitScreen} {
    align-items: flex-start;
    flex-direction: column;
    gap: 16px;
    justify-content: flex-start;

    > :first-child {
      padding-top: 0;
    }
  }
`;

const SectionSubSetting = styled.div`
  border-top: 1px solid var(--greyscale-dark-white);
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  padding-top: 20px;
`;

const SectionTitle = styled.h4`
  font-size: var(--font-size-heading-4);
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-regular);
  margin: 0;
`;

const StepBlock = styled.div`
  border-top: 1px solid var(--greyscale-dark-white);
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px;

  &:last-child {
    border-bottom: 1px solid var(--greyscale-dark-white);
  }
`;

const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 48px;
`;

const StepCircle = styled.div`
  align-items: center;
  border: 1.5px solid var(--greyscale-light-grey);
  border-radius: var(--border-radius-circle);
  color: var(--greyscale-blackish);
  display: flex;
  flex-shrink: 0;
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-medium);
  height: 32px;
  justify-content: center;
  width: 32px;
`;

const StepDescription = styled.div`
  color: var(--greyscale-shade);
  font-size: var(--font-size-regular);
  line-height: var(--font-line-height-regular);
  max-width: 540px;
`;

const StepHead = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const StepHeadLeft = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 16px;
  min-width: 0;
`;

const StepTitle = styled.div`
  color: var(--greyscale-blackish);
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-regular);
`;

const TabBar = styled.div`
  border-bottom: 1px solid var(--greyscale-dark-white);
  display: flex;
  gap: 20px;
  padding: 0 20px;

  @media ${windowMaxWidth.splitScreen} {
    padding: 0 16px;
  }
`;

const TabItem = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--greyscale-shade);
  cursor: pointer;
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-regular);
  line-height: var(--font-line-height-regular);
  margin-bottom: -1px;
  padding: 10px 5px;
  transition: color 0.15s ease;
  white-space: nowrap;

  &:hover {
    color: var(--greyscale-blackish);
  }

  ${({ $active }) =>
    $active &&
    `
    border-bottom-color: var(--greyscale-blackish);
    color: var(--greyscale-blackish);
    font-weight: var(--font-weight-medium);
  `}

  @media ${windowMaxWidth.splitScreen} {
    font-size: var(--font-size-medium);
    padding: 10px;
  }
`;

const TabPageWrapper = styled.div`
  background-color: var(--greyscale-white);
  border: 1px solid var(--greyscale-dark-white);
  border-radius: var(--border-radius-regular);
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const ToggleRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 40px;
  justify-content: space-between;
`;

const ContentLayout = {
  AccordionBadge,
  AccordionContent,
  AccordionDescription,
  AccordionHeader,
  AccordionHeaderText,
  AccordionIconWrapper,
  CheckboxGrid,
  ContentHeader,
  ContentIntro,
  ContentIntroMeta,
  ContentPadded,
  ContentTitle,
  FieldLabel,
  NotificationCheckboxCell,
  NotificationColumnHead,
  NotificationColumnSubtitle,
  NotificationColumnTitle,
  NotificationDescription,
  NotificationHeaderRow,
  NotificationHelpLink,
  NotificationOptionIcon,
  NotificationRow,
  NotificationRowLabel,
  NotificationTable,
  ReadOnlyRow,
  ReadOnlyValue,
  SaveRow,
  SaveFooter,
  SectionBody,
  SectionCard,
  SectionDescription,
  SectionLeft,
  SectionRight,
  SectionRightContent,
  SectionRow,
  SectionSubSetting,
  SectionTitle,
  StepBlock,
  StepBody,
  StepCircle,
  StepDescription,
  StepHead,
  StepHeadLeft,
  StepTitle,
  TabBar,
  TabItem,
  TabPageWrapper,
  ToggleRow
};

export default ContentLayout;
